import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/kit/PageHeader";
import { cn } from "@/lib/utils";
import { BillingTenant } from "@/components/settings/BillingTenant";
import { ProfileSettings } from "@/components/settings/ProfileSettings";
import { AccountSecurity } from "@/components/settings/AccountSecurity";
import { NotificationsCenter } from "@/components/settings/NotificationsCenter";
import { PlatformSettings } from "@/components/settings/MiscSettings";
import { Branding } from "@/components/settings/Branding";
import { PaymentProviders } from "@/components/settings/PaymentProviders";
import { RegistrationSettings } from "@/components/settings/tenant/sections";
import { OrganizationProfile } from "@/components/settings/tenant/OrganizationProfile";
import { AffiliatePortal } from "@/components/settings/tenant/AffiliatePortal";
import { FilterLandingsLibrary } from "@/components/settings/tenant/FilterLandingsLibrary";

import { SecurityTenant } from "@/components/settings/tenant/SecurityTenant";
import { VerticalsTab, SourcesTab, PaymentMethodsTab } from "@/components/settings/tenant/resourceTabs";

const TABS_BY_ROLE = {
  super_admin: [
    { id: "my-profile", label: "My Profile", render: () => <ProfileSettings /> },
    { id: "security", label: "Security", render: () => <AccountSecurity /> },
    { id: "platform", label: "Platform", render: () => <PlatformSettings /> },
    { id: "providers", label: "Payment Providers", render: () => <PaymentProviders /> },
    { id: "notifications", label: "Notifications", render: () => <NotificationsCenter /> },
    { id: "branding", label: "Branding", render: () => <Branding /> },
  ],
  tenant_admin: [
    { id: "my-profile", label: "My Profile", render: () => <ProfileSettings /> },
    { id: "profile", label: "Organization Profile", render: () => <OrganizationProfile /> },
    { id: "security", label: "Security", render: () => <SecurityTenant /> },
    { id: "portal", label: "Affiliate Portal", render: () => <AffiliatePortal /> },
    { id: "registration", label: "Registration", render: () => <RegistrationSettings /> },
    { id: "landings", label: "Filter Landings", render: () => <FilterLandingsLibrary /> },
    { id: "verticals", label: "Verticals", render: () => <VerticalsTab /> },
    { id: "sources", label: "Sources", render: () => <SourcesTab /> },
    { id: "payments", label: "Payment Methods", render: () => <PaymentMethodsTab /> },
    { id: "notifications", label: "Notifications", render: () => <NotificationsCenter /> },
    { id: "billing", label: "Billing", render: () => <BillingTenant /> },
  ],
  manager: [
    { id: "profile", label: "Profile", render: () => <ProfileSettings /> },
    { id: "security", label: "Security", render: () => <AccountSecurity /> },
    { id: "notifications", label: "Notifications", render: () => <NotificationsCenter /> },
  ],
  buyer: [
    { id: "profile", label: "Profile", render: () => <ProfileSettings /> },
    { id: "security", label: "Security", render: () => <AccountSecurity /> },
    { id: "notifications", label: "Notifications", render: () => <NotificationsCenter /> },
  ],
};

const SUBTITLE = {
  super_admin: "Platform configuration & system settings.",
  tenant_admin: "Configure your affiliate program — organization, affiliates, branding & more.",
  manager: "Your personal account settings.",
  buyer: "Your personal account settings.",
};

function PlatformPlaceholder({ title, desc }) {  // eslint-disable-line no-unused-vars
  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-10 text-center" data-testid="settings-placeholder">
      <h3 className="text-sm font-medium text-foreground">{title}</h3>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">{desc}</p>
      <span className="mt-3 inline-block rounded-full border border-white/[0.1] px-3 py-1 text-xs text-muted-foreground">Coming soon</span>
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tabs = useMemo(() => TABS_BY_ROLE[user?.role] || TABS_BY_ROLE.buyer, [user?.role]);
  const initial = params.get("tab") && tabs.find((t) => t.id === params.get("tab")) ? params.get("tab") : tabs[0].id;
  const [active, setActive] = useState(initial);
  const current = tabs.find((t) => t.id === active) || tabs[0];
  const select = (id) => { setActive(id); setParams({ tab: id }, { replace: true }); };

  return (
    <div className="space-y-6">
      <PageHeader title="Settings" subtitle={SUBTITLE[user?.role] || ""} />
      <div className="no-scrollbar flex flex-wrap gap-1 overflow-x-auto border-b border-white/[0.08]">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => select(t.id)} data-testid={`settings-tab-${t.id}`}
            className={cn("relative whitespace-nowrap px-3 py-2 text-sm transition-colors", active === t.id ? "text-foreground" : "text-muted-foreground hover:text-foreground")}>
            {t.label}{active === t.id && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-white" />}
          </button>
        ))}
      </div>
      <div data-testid={`settings-panel-${current.id}`}>{current.render()}</div>
    </div>
  );
}
